//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import angular from 'angular';

import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import { SocialNetwork } from 'app/models/SocialNetwork';
import { hasRightsByCreationSource } from '@legacy-components/common/universal-image-actions/checkItemRightsStatus';
import {
  formatTikTokEmbedTitle,
  formatYouTubeEmbedTitle,
} from '@legacy-components/common/universal-image-actions/formatEmbedTitle';
import { bvTnCUrls } from '@legacy-components/rights-management/request-rights-util';
import { isDefined } from '@utils/filterPredicates';
import { muteCaptionDisclaimerMessage } from 'app/constants/mute-tooltip-content';
import { originalAngularBootstrap } from 'app/initialization/angular/original-bootstrap';
import { getFeatureValue } from 'app/utils/featureStorage';
import { FeatureStorageScope } from 'app/utils/featureStorageModel';
import LEGACY_TOAST_TEMPLATE from '@legacy-components/common/toast-notifications/templates/new-toast.html';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';

import { YouTubeWebLinks } from 'app/constants/social-links';
import 'app/components/common/universal-image-actions/UniversalImageActionsAudioButton';
import 'app/components/common/universal-image-actions/UniversalImageActionsCaptionButton';
import 'app/components/common/universal-image-actions/UniversalImageActionsDownloadButton';
import 'app/components/common/universal-image-actions/UniversalImageActionsUndoBeautifyButton';
import '@uia/intelligent-product-tagging/IntelligentProductTaggingSearchLoader';
import 'app/universal-image-actions-modal/distribute/UIADistribute';
import 'app/universal-image-actions-modal/custom-cover-image/UniversalImageActionsCustomCoverImage';

import {
  muteSelectedItems,
  processCaptioningOnSelectedItems,
  getCaptionStatuses,
} from '@apis/video-processing/videoProcessingApi';
import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';
import * as shareApi from '@share/api/shareApi';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { setRightsStatusChanged } from '@uia/rights-request/rights-request-actions';
import {
  setLastUpdateLabels,
  setLaunchOptions,
} from '@uia/uia-modal/modal-slice';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import universalImageActionsModalTemplate from '@legacy-components/common/universal-image-actions/universal-image-actions-modal.html';

(() => {
  const curalate = angular.module('curalate');
  const uia = angular.module('uia');

  /**
   * universal-image-actions-bootstrapper
   *
   * Simple abstraction connecting the "curalate" module to the "uia" module.
   * Bootstraps the "uia" Angular app to create the modal context.
   */
  curalate.directive('universalImageActionsBootstrapper', () => {
    return {
      restrict: 'E',
      scope: {},
      template:
        '<universal-image-actions-modal></universal-image-actions-modal>',
      link($scope, $element) {
        // Use the original AngularJs bootstrap here, not the ocLazyLoad override.
        // the ocLazyLoad's version overrides some of its global state in its bootstrap override.
        // Since we're not using ocLazyLoad within UIA itself, it should be safe to skip the custom bootstrap.
        originalAngularBootstrap(
          $element.find('universal-image-actions-modal'),
          ['uia'],
        );
      },
    };
  });

  /**
   * universal-image-actions-modal
   *
   * The main (root) directive of the "uia" app. Responsible for modal initialization and general modal interactions.
   */
  uia.directive('universalImageActionsModal', () => {
    return {
      restrict: 'E',
      scope: {},
      template: universalImageActionsModalTemplate,
      controller: [
        '$ngRedux',
        '$q',
        '$rootScope',
        '$sce',
        '$scope',
        '$templateCache',
        '$state',
        '$timeout',
        '$window',
        'AssistedProductTaggingService',
        'ContentLibraryApiService',
        'ErrorNotificationService',
        'ImageMetadataService',
        'LabelsService',
        'NotesService',
        'ProductTaggingService',
        'RightsStatus',
        'SchedulerService',
        'SelectedItemActions',
        'SessionVarsService',
        'ShowroomApiService',
        'ShowroomService',
        'SyndicationService',
        'TrackingService',
        'UniversalImageActionsApiService',
        'UniversalImageActionsService',
        'UniversalImageActionsWorkflowService',
        'UniversalImageActionsHelpers',
        'UniversalImageActionsItemType',
        'UniversalImageActionsView',
        'UniversalImageActionWorkflows',
        'KEY_CODES',
        'TOASTS',
        'ToastNotificationService',
        'RightsManagementService',
        '$location',
        'CampaignPerspectiveService',
        function (
          $ngRedux,
          $q,
          $rootScope,
          $sce,
          $scope,
          $templateCache,
          $state,
          $timeout,
          $window,
          AssistedProductTaggingService,
          ContentLibraryApiService,
          ErrorNotificationService,
          ImageMetadataService,
          LabelsService,
          NotesService,
          ProductTaggingService,
          RightsStatus,
          SchedulerService,
          SelectedItemActions,
          SessionVarsService,
          ShowroomApiService,
          ShowroomService,
          SyndicationService,
          TrackingService,
          UniversalImageActionsApiService,
          UniversalImageActionsService,
          UniversalImageActionsWorkflowService,
          UniversalImageActionsHelpers,
          UniversalImageActionsItemType,
          UniversalImageActionsView,
          UniversalImageActionWorkflows,
          KEY_CODES,
          TOASTS,
          ToastNotificationService,
          RightsManagementService,
          $location,
          CampaignPerspectiveService,
        ) {
          const self = this;
          let showroomPromise = null;
          /**
           * TODO: PD-301920 - Cleanup IPT v1 specific code once IPTv2 GAed
           * Both flags can be enabled for transitioning/test clients. We want to kick off the
           * product relevancy asynchronous data call if either flag is enabled. v2 takes precedence
           * in AssistedProductTaggingService.
           */
          const isIntelligentProductTaggingV2Enabled =
            SessionVarsService.isFeatureEnabled(
              FeatureNames.IntelligentProductTaggingV2,
            );
          const isIntelligentProductTaggingEnabled =
            SessionVarsService.isFeatureEnabled(
              FeatureNames.UiaIntelligentProductTagging,
            ) || isIntelligentProductTaggingV2Enabled;
          const isUiaPermalinksEnabled = SessionVarsService.isFeatureEnabled(
            FeatureNames.UiaPermalinks,
          );
          // TODO - PD-265514 - Remove feature flag post GA
          const isXMPTaggingEnabled = SessionVarsService.isFeatureEnabled(
            FeatureNames.XmpMetadataTagging,
          );
          const launchOptions =
            UniversalImageActionsService.getCurrentLaunchOptions();
          const launchReel = launchOptions.getLaunchReel();
          const launchView = launchOptions.getLaunchView();
          const launchWorkflow = launchOptions.getLaunchWorkflow();
          const TRACKING_CATEGORY =
            UniversalImageActionsHelpers.getTrackingCategory(
              UniversalImageActionsService.getCurrentPage(),
            );

          getNgRedux().connect(
            (state) => {
              if (state.uiaModal.modalState.lastUpdatedLabels) {
                $scope.$applyAsync(() => {
                  $scope.model.labels =
                    state.uiaModal.modalState.lastUpdatedLabels;
                  $scope.model.labelCount = $scope.model.labels.length;
                  getNgRedux().dispatch(setLastUpdateLabels());
                });
              }
              if (state.uiaModal.rightsRequest.lastUpdatedStatus) {
                $scope.$applyAsync(() => {
                  $scope.model.itemMetadata.rightsRequestStatus.status =
                    state.uiaModal.rightsRequest.lastUpdatedStatus;
                  getNgRedux().dispatch(setRightsStatusChanged());
                });
              }
              return {};
            },
            () => {},
          )(this);

          const defaultToastOptions = TOASTS.DEFAULT_TOAST_OPTIONS;
          let desiredHeight;
          let desiredWidth;
          let desiredAspectRatio;
          let videoHeight;
          let videoWidth;
          let videoAspectRatio;

          const isShareV2VendorClient =
            SessionVarsService.isShareV2VendorClient();
          const isBazaarvoiceSyndicationEnabled =
            SessionVarsService.isFeatureEnabled(
              FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
            );
          const isLegacyShareV2VendorClient =
            isShareV2VendorClient && !isBazaarvoiceSyndicationEnabled;
          const isBazaarvoiceSyndicationOnlyClient =
            isShareV2VendorClient && isBazaarvoiceSyndicationEnabled;

          $templateCache.put(
            'directives/toast/simplebar.html',
            LEGACY_TOAST_TEMPLATE,
          );

          const VIDEO_MUTESTATUS = 'Muted';
          const TIKTOK_ASPECT_RATIO = 9 / 16;
          const YOUTUBE_ASPECT_RATIO = 16 / 9;
          $scope.model = {
            launched: false,
            features: UniversalImageActionsService.getCurrentFeatureSet(),
            itemMetadata: {},
            downloadUrl: '',
            downloadFilename: '',
            hasFetchedDownloadUrl: false,
            isProcessingDownload: false,
            hasAudioshared: false,
            isProcessingAudio: false,
            selectedAction: launchView,
            rightsRequestState: '',
            isVideoPlaying: false,
            beautified: false,
            loading: true,
            currentlyInWorkflow: false,
            isVideoCaptioned: false,
            isProcessingCaption: false,
            outstandingActions: {
              addToLibrary: false,
            },
            animations: {
              bootstrap: true,
              next: false,
              back: false,
            },
            ESC_KEYMAP: [KEY_CODES.ESC],
            areSocialDashboardsEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.SocialDashboards,
            ),
            isAddLabelsEnabled: launchOptions.isAddLabelsEnabled,
            isShareItem:
              launchOptions.getItemType() ===
              UniversalImageActionsItemType.syndication,
            isSyndicationClient: SessionVarsService.isSyndicationClient(),
            isNotesEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.Notes,
            ),
            isRevealEnabled:
              SessionVarsService.isFeatureEnabled(FeatureNames.RevealPage) ||
              SessionVarsService.isSyndicationClient(),
            isTributaryEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.TributarySelfServe,
            ),
            isShowroomSelfServeV2Enabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.ShowroomSelfServeV2,
            ),
            isSchedulerEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.InstagramScheduler,
            ),
            isGalleriesEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.Galleries,
            ),
            isInstaShopEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.Instashop,
            ),
            isRightsManagementEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.RightsManagementPage,
            ),
            isLegacyShareV2VendorClient: isLegacyShareV2VendorClient,
            isBazaarvoiceSyndicationEnabled: isBazaarvoiceSyndicationEnabled,
            isBazaarvoiceSyndicationOnlyClient:
              isBazaarvoiceSyndicationOnlyClient,
            isShareV2VendorClient: isShareV2VendorClient,
            isOnePageSelfServeEnabled: SessionVarsService.isFeatureEnabled(
              FeatureNames.OnePageSelfServe,
            ),
            hasClientUsedOnePage: getFeatureValue(
              FeatureStorageScope.COPIED_ONE_PAGE_LINK,
            ),
            isBrandPerspectiveView: true, // Default to true until campaign perspective is determined from URL
            // Type info for this prop => embed: undefined | {
            //   link: string;
            //   aspectRatio: number;
            //   title: string;
            // }
            embed: undefined,
            cuid: launchOptions.getUniversalItemId(),
            sourceNetwork: launchOptions.getSourceNetwork(),
            showroomExists: false,
            showroomHasBeenUpdated: false,
            showroomHasProducts: false,
            uiaLaunchContext: UniversalImageActionsService.getCurrentPage(),
            // Consts used in the record-click-event directive
            CATEGORY_BEAUTIFY: TrackingService.CATEGORY_BEAUTIFY,
            ACTION_BEAUTIFY_UNDO_EDITS:
              TrackingService.ACTION_BEAUTIFY_UNDO_EDITS,
            isUrlNotInMessage: false,
            videoDuration: undefined,
          };
          ($scope.model.isBazaarvoiceNetworkSyndicationOnlyFullClient =
            SessionVarsService.isFeatureEnabled(
              FeatureNames.BazaarvoiceNetworkSyndicationOnlyFullClient,
            )),
            ($scope.model.clientHasLibraryAccess =
              !$scope.model.isSyndicationClient &&
              SessionVarsService.isFeatureEnabled(
                FeatureNames.MediaLibraryPage,
              ));

          $scope.model.muteCaptionDisclaimerMessage =
            muteCaptionDisclaimerMessage;

          const getAvailableDimensions = function () {
            const imageUiaWrapper = angular.element('.image-uia-wrapper');

            // Take the image out of the DOM flow so it doesn't affect it's parent's size when we measure
            const uiaImage = imageUiaWrapper.find('.uia-image');
            const uiaImageOriginalPosition = uiaImage.css('position');
            uiaImage.css('position', 'absolute');

            const availableWidth = imageUiaWrapper.width() * 0.9;
            const availableHeight = imageUiaWrapper.height() * 0.9;

            uiaImage.css('position', uiaImageOriginalPosition);

            return {
              width: availableWidth,
              height: availableHeight,
              aspectRatio: availableWidth / availableHeight,
            };
          };

          $scope.model.shouldShowCustomCoverImage = function () {
            if (
              $scope.model.currentlyInWorkflow ||
              !$scope.model.itemMetadata ||
              !$scope.model.isGalleriesEnabled
            ) {
              return false;
            }

            const socialNetwork = $scope.model.itemMetadata.socialNetwork;
            if (
              socialNetwork === SocialNetwork.TikTok ||
              socialNetwork === SocialNetwork.YouTube
            ) {
              return $scope.model.itemMetadata.isBrandOwned;
            }

            if (!$scope.model.videoSource) {
              return false;
            }

            return true;
          };

          $scope.model.closeViaHotkey = function () {
            const workflow = UniversalImageActionsService.getCurrentWorkflow();

            if (workflow) {
              $scope.model.selectAction('Home');
            } else {
              $scope.model.close();
            }
          };

          const calculateTotalMediaSize = function () {
            const calculationHeight = videoHeight || desiredHeight;
            const calculationWidth = videoWidth || desiredWidth;
            const calculationAspectRatio =
              videoAspectRatio || desiredAspectRatio;

            const availableDimensions = getAvailableDimensions();

            $scope.$applyAsync(() => {
              if (availableDimensions.aspectRatio > calculationAspectRatio) {
                $scope.model.calculateHeight = Math.min(
                  calculationHeight,
                  availableDimensions.height,
                );
                $scope.model.calculateWidth =
                  $scope.model.calculateHeight * calculationAspectRatio;
              } else {
                $scope.model.calculateWidth = Math.min(
                  calculationWidth,
                  availableDimensions.width,
                );
                $scope.model.calculateHeight =
                  $scope.model.calculateWidth / calculationAspectRatio;
              }
            });
          };

          $scope.RightsStatus = RightsStatus;

          $scope.model.getRightsStatus = function (itemMetadata) {
            let rightsStatus;
            const existingRightsPermissionsSource =
              itemMetadata &&
              itemMetadata.rightsPermissions &&
              itemMetadata.rightsPermissions.creationSource;

            if (hasRightsByCreationSource(itemMetadata)) {
              rightsStatus = RightsStatus.Approved;
            } else {
              rightsStatus =
                itemMetadata.rightsRequestStatus &&
                itemMetadata.rightsRequestStatus.status;
            }

            switch (rightsStatus) {
              case RightsStatus.Approved:
              case RightsStatus.BrandOwned:
                return rightsStatus;
              default:
                if (
                  itemMetadata.socialNetwork === 'Instagram' &&
                  !itemMetadata.originalUrl
                ) {
                  // We don't have an original URL for this image, show not supported state.
                  return RightsStatus.NotSupported;
                }
                return rightsStatus;
            }
          };

          $scope.model.getRightsRequestClass = function () {
            let className = 'di-requestrights-default-32px';

            if (
              $scope.model.itemMetadata &&
              $scope.model.itemMetadata.rightsRequestStatus &&
              $scope.model.itemMetadata.rightsRequestStatus.status
            ) {
              switch ($scope.model.getRightsStatus($scope.model.itemMetadata)) {
                case RightsStatus.Queued:
                  className = 'di-requestrights-pending-32px queued-icon';
                  break;
                case RightsStatus.Pending:
                  className = 'di-requestrights-pending-32px';
                  break;
                case RightsStatus.Initiated:
                  className = 'di-requestrights-pending-32px';
                  break;
                case RightsStatus.Approved:
                  className =
                    'di-requestrights-approved-32px generic-approved-icon';
                  break;
                case RightsStatus.BrandOwned:
                  className =
                    'di-requestrights-approved-32px brand-approved-icon';
                  break;
                case RightsStatus.NotSupported:
                  className = 'di-rr-na-16px';
                  break;
                case RightsStatus.Rejected:
                case RightsStatus.FailedToSend:
                  className = 'di-requestrights-rejected-32px';
                  break;
                case RightsStatus.Unsent:
                default:
                  className = 'di-requestrights-default-32px';
                  break;
              }
            }

            return className;
          };

          this.resetAnimations = function () {
            $scope.model.animations.bootstrap = true;
            $scope.model.animations.next = false;
            $scope.model.animations.back = false;
          };

          this.animateForward = function () {
            $scope.model.animations.bootstrap = false;
            $scope.model.animations.next = true;
            $scope.model.animations.back = false;
          };

          this.animateBackward = function () {
            $scope.model.animations.bootstrap = false;
            $scope.model.animations.next = false;
            $scope.model.animations.back = true;
          };

          function goToWorkflowStep(state) {
            const workflow = UniversalImageActionsService.getCurrentWorkflow();

            workflow.goToState(state);
            $state.go(`distribute.${state}`);
            $scope.model.selectedAction = 'Home';
          }

          function setDownloadDetails(downloadUrl, filename) {
            $scope.$apply(() => {
              if (isXMPTaggingEnabled && $scope.model.itemMetadata.rootCuid) {
                $scope.model.downloadUrl = `${downloadUrl}?cuid=${$scope.model.itemMetadata.rootCuid}`;
              } else {
                $scope.model.downloadUrl = downloadUrl;
              }
              $scope.model.downloadFilename = filename;
              $scope.model.hasFetchedDownloadUrl = true;
            });
          }

          // Define this event handler before initialization so it gets called when initializing the modal into a workflow
          $scope.$on('uia:workflow-changed', (event, workflow) => {
            $scope.model.currentlyInWorkflow = _.isObject(workflow);
          });

          $scope.model.close = function (result) {
            let beautifyUrl;
            let currentProducts;
            let currentReelTags;
            let itemApproved;
            let newNotes;
            let originalProducts;
            let originalReelTags;
            let productsChanged;
            let reelTagsChanged;
            let video;

            $window.removeEventListener('resize', calculateTotalMediaSize);

            ToastNotificationService.close();
            if ($scope.model.loading) {
              // If an exception is thrown, the modal was closed before it was loaded, and there is no result
              launchOptions.close();
            } else {
              if ($scope.model.videoSource && $scope.model.isVideoPlaying) {
                // Fun tricks for preventing videos from continuing to download once you've removed them from the DOM:
                // http://stackoverflow.com/questions/4071872/html5-video-force-abort-of-buffering
                $scope.model.videoSource = '';
                $scope.model.isVideoPlaying = false;

                video = angular.element('#uia-video').get(0);
                video.pause(0);
                video.src = '';
                video.load();
                angular.element('#uia-video').remove();
              }

              if ($scope.model.beautified) {
                beautifyUrl = $scope.model.displayImageUrl;
              }

              currentProducts = ProductTaggingService.getProducts();
              originalProducts = ProductTaggingService.getOriginalProducts();
              productsChanged = !angular.equals(
                currentProducts,
                originalProducts,
              );

              currentReelTags = UniversalImageActionsService.getReelTags();
              originalReelTags =
                UniversalImageActionsService.getOriginalReelTags();
              reelTagsChanged = !angular.equals(
                currentReelTags,
                originalReelTags,
              );

              itemApproved = result && result.itemWasApprovedForFanreel;

              newNotes = NotesService.getNotesSync();

              launchOptions.close({
                universalItemId: launchOptions.getUniversalItemId(),
                itemId: launchOptions.getItemId(),
                products: currentProducts,
                reelTags: currentReelTags,
                productsChanged,
                reelTagsChanged,
                userTags: $scope.model.labels,
                itemApproved,
                beautifyUrl,
                newNotes,
                result,
              });
            }

            // Fun fact: ui-bootstrap $modal.close() does not seem to trigger $destroy on the scope of the directive used in the modal.
            // Since we bootstrap() a new angular app instance with the universal-image-actions-bootstrapper, we need to tear it down when we're
            // done or face memory leaks and other strange behavior--such as
            //    orphaned watchers for the $location service triggering on URL changes

            // Tear down the rootscope of this application instance.
            $rootScope.$destroy();
          };
          $scope.model.activateWorkflow = function (workflow) {
            const workflowObj =
              UniversalImageActionsWorkflowService.workflowForName(workflow);
            UniversalImageActionsService.setCurrentWorkflow(workflowObj);

            self.animateForward();
            goToWorkflowStep(workflow);
          };

          const checkRightsMessageForTermsAndConditionsUrl = () => {
            if (
              SessionVarsService.isFeatureEnabled(
                FeatureNames.RightsRequestsTermsAndConditions,
              )
            ) {
              const clientId = SessionVarsService.getClientId();

              shareApi
                .getIsSyndicatedContentStatus(
                  clientId,
                  $scope.model.itemMetadata.rootCuid,
                )
                .then((status) => {
                  $scope.model.isSyndicated = status.isSyndicated;

                  const workflow =
                    UniversalImageActionsService.getCurrentWorkflow();

                  // If the Rights Request Terms and Conditions feature flag is enabled, the button clicked
                  // is the RightsRequest button, the user is on the Visual Syndication Network flow and the request
                  // status is either 'expired', 'initiated', 'approved' or 'pending'. We then retrieve all
                  // urls (current and previous) and check if the message doesn't contain any of the URLs,
                  // if that's the case we set $scope.model.isUrlInMessage to true.
                  const requestStatusList = [
                    'expired',
                    'initiated',
                    'approved',
                    'pending',
                  ];

                  if (
                    ($scope.model.isSyndicated ||
                      (workflow && workflow.name !== 'ApproveForGalleries') ||
                      isBazaarvoiceSyndicationOnlyClient) &&
                    requestStatusList.includes(
                      $scope.model.itemMetadata.rightsRequestStatus.status,
                    )
                  ) {
                    const message =
                      $scope.model.itemMetadata.rightsRequestStatus
                        .requestMessage;
                    $q.all([
                      UniversalImageActionsService.getRightsRequestAsync(),
                      RightsManagementService.getHistoricalUrls(),
                    ]).then((response) => {
                      const communicationType = response[0].communicationType;
                      const allUrls = response[1];
                      const hasNoUrlsInMessage =
                        allUrls &&
                        allUrls.every(
                          (url) => !message.includes(url.termsAndConditionsUrl),
                        );
                      const hasNoBVTermsAndConditionUrlsInMessage =
                        bvTnCUrls.every((url) => !message.includes(url));
                      if (
                        !(
                          communicationType === 'BV_SAMPLING_CAMPAIGN' &&
                          $scope.model.itemMetadata.rightsRequestStatus
                            .status === RightsStatus.Approved
                        ) &&
                        hasNoUrlsInMessage &&
                        hasNoBVTermsAndConditionUrlsInMessage
                      ) {
                        $scope.model.isUrlNotInMessage = true;
                      }
                    });
                  }
                });
            }
          };

          $scope.model.selectAction = function (action) {
            if (
              $scope.model.selectedAction === action &&
              $state.current.name === action
            ) {
              return;
            } else if ($scope.model.currentlyInWorkflow && action !== 'Home') {
              return;
            }

            switch (action) {
              case 'Home':
                UniversalImageActionsService.trackMediaEvent(
                  MediaTrackingEventName.DistributeClicked,
                );
                break;
              default:
                break;
            }

            self.resetAnimations();
            UniversalImageActionsService.setCurrentWorkflow(null);
            UniversalImageActionsService.resetModalSessionTimer();
            $scope.model.selectedAction = action;
            $state.go(action);
          };

          // TODO: UIA - cleanup; factor this method into a common location because it is used by both the overlay and modal (and other places, too..)
          $scope.model.addToLibrary = function ($event) {
            let overrideImageUrl = null;

            if ($scope.model.outstandingActions.addToLibrary) {
              return;
            }

            $event.preventDefault();
            $event.stopPropagation();

            $scope.model.outstandingActions.addToLibrary = true;

            if ($scope.model.beautified) {
              overrideImageUrl =
                UniversalImageActionsService.getCanonicalImageUrl();
            }

            UniversalImageActionsService.trackMediaEvent(
              MediaTrackingEventName.AddedToLibrary,
            );

            UniversalImageActionsApiService.addToLibraryAsync(
              launchOptions.getUniversalItemId(),
              overrideImageUrl,
            )
              .then((response) => {
                const toastArgs = {
                  title: 'Media Added',
                  message: 'Your media was added to the media library',
                  type: ToastType.SUCCESS,
                  options: {
                    allowHtml: true,
                    toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
                    extraData: {
                      callback: () => navigateToState('library.team-media'),
                      linkText: 'View in Media Library',
                    },
                  },
                };

                if (
                  launchOptions.getItemType() ===
                  UniversalImageActionsItemType.syndication
                ) {
                  SyndicationService.saveUserImageActivity(
                    launchOptions.getItemId(),
                    'addToLibrary',
                  );
                }

                TrackingService.recordEvent(
                  TRACKING_CATEGORY,
                  TrackingService.ACTION_ADD_TO_LIBRARY,
                  $scope.model.videoSource ? 'video' : undefined,
                );

                $scope.model.close({
                  addToLibrary: response,
                  successToast: toastArgs,
                });
              })
              .catch((error) => {
                if (error.message) {
                  ErrorNotificationService.showDefaultErrorDialog(
                    error.message,
                    error,
                  );
                } else {
                  ErrorNotificationService.showDefaultErrorDialog(error);
                }
              })
              .finally(() => {
                $scope.model.outstandingActions.addToLibrary = false;
              });
          };

          $scope.model.handleBeautifyChange = function (newUrl, newFileId) {
            const currentProducts = ProductTaggingService.getProducts();

            $scope.model.beautifyOpen = false;

            const hasSpatialTags = _(currentProducts).any((product) => {
              return product.spatialTag;
            });

            if (hasSpatialTags) {
              _(currentProducts).map((product) => {
                delete product.spatialTag;
                return product;
              });
              ProductTaggingService.setProducts(currentProducts);
            }

            $scope.model.outstandingAsyncAction = 'beautify';
            UniversalImageActionsApiService.beautifyImageAsync(
              launchOptions.getUniversalItemId(),
              newUrl,
              newFileId,
            )
              .then((response) => {
                if ($scope.model.beautified) {
                  $scope.beautifiedImage = response;
                  UniversalImageActionsService.setBeautifiedImage(response);
                } else {
                  $scope.beautifiedImage = null;
                  UniversalImageActionsService.revertBeautifiedImage(response);
                }
              })
              .catch((error) => {
                ErrorNotificationService.showDefaultErrorDialog(error);
              })
              .finally(() => {
                $scope.model.outstandingAsyncAction = null;
              });
          };

          $scope.model.beautifyClicked = function () {
            $scope.model.beautifyOpen = true;
          };

          $scope.model.getForceCropRatio = function () {
            const workflow = UniversalImageActionsService.getCurrentWorkflow();
            const item = self.getScheduleNowState();
            const dimensions = UniversalImageActionsHelpers.getImageDimensions(
              angular.element('#beautify-image-uia')[0],
            );

            if (
              workflow &&
              workflow.name === UniversalImageActionsView.ScheduleNow.name
            ) {
              return SchedulerService.getForceCropRatio(item, dimensions);
            }

            return null;
          };

          $scope.model.shouldShowBeautifyButton = function () {
            const currentWorkflow =
              UniversalImageActionsService.getCurrentWorkflow();
            const notOnProductizePanel =
              !currentWorkflow ||
              currentWorkflow.getCurrentState() !==
                UniversalImageActionsView.Productize.name;
            const notOnRevealPanel =
              !currentWorkflow ||
              currentWorkflow.getCurrentState() !==
                UniversalImageActionsView.Reveal.name;
            const notOnTributaryPanel =
              !currentWorkflow ||
              currentWorkflow.getCurrentState() !==
                UniversalImageActionsView.Tributary.name;

            // Only show the distribute panel when the user is in a distribution workflow, and hide it inside of the productization or reveal panels
            return (
              !$scope.model.loading &&
              !$scope.model.itemMetadata.videoSource &&
              $scope.model.currentlyInWorkflow &&
              notOnProductizePanel &&
              notOnRevealPanel &&
              notOnTributaryPanel
            );
          };

          $scope.model.beautifySaveClicked = function (newUrl) {
            $scope.model.beautified = true;
            $scope.model.handleBeautifyChange(newUrl);
          };

          $scope.model.revertBeautify = function () {
            $scope.model.beautified = false;
            $scope.model.handleBeautifyChange(
              $scope.model.itemMetadata.imageUrl,
              $scope.model.itemMetadata.fileId,
            );
          };
          $scope.model.clickmuteUnmuteSelectedItems = function () {
            $scope.$applyAsync(() => {
              $scope.model.isProcessingAudio = true;
            });
            try {
              const cuids = launchOptions.getUniversalItemId().split(' ');
              const muteValue = $scope.model.isMuted ? false : true;
              const itemIds = [launchOptions.getUniversalItemId()];
              // Make an asynchronous call to the backend
              const promise = muteSelectedItems(muteValue, cuids);

              promise
                .then(function (response) {
                  // Update the view with the response data
                  $scope.model.responseData = response;

                  // Start polling for videoMuteStatus
                  $scope.result = '';
                  var pollingInterval = setInterval(function () {
                    ContentLibraryApiService.getContentItemsAsync({
                      itemIds,
                    }).then(function (response) {
                      const item = response.contentItems[0];
                      const videoUserName = item.misDetails
                        ? item.misDetails.user.fullName
                        : item.socialDetails.user.username;
                      if (item && muteValue === false) {
                        // Set isProcessingAudio to false after the operation completes
                        $scope.model.isProcessingAudio = false;
                        $scope.model.isMuted = false;
                        clearInterval(pollingInterval);
                      } else if (
                        item &&
                        item.metadata.videoMuteStatus === VIDEO_MUTESTATUS &&
                        muteValue === true
                      ) {
                        $scope.model.isProcessingAudio = false;
                        $scope.model.isMuted =
                          item.metadata.videoMuteStatus == VIDEO_MUTESTATUS
                            ? true
                            : false;
                        clearInterval(pollingInterval);
                        if (item.metadata.videoMuteStatus === 'Failed') {
                          ErrorNotificationService.showDefaultErrorDialog(
                            "We weren't able to mute this video. Please try again.",
                          );
                        }
                      }
                      $scope.model.videoSource = $sce.trustAsResourceUrl(
                        item.metadata.videoSource,
                      );
                      UniversalImageActionsApiService.getVideoDownloadUrl(
                        item.videoId,
                        $scope.model.isMuted,
                      ).then((downloadUrl) => {
                        setDownloadDetails(
                          downloadUrl,
                          UniversalImageActionsApiService.getVideoDownloadFilename(
                            videoUserName,
                          ),
                        );
                      });
                    });
                  }, 5000);

                  $scope.$on('$destroy', function () {
                    // Ensure polling is stopped when the controller is destroyed
                    clearInterval(pollingInterval);
                  });
                })
                .catch(function (err) {
                  ErrorNotificationService.showDefaultErrorDialog(err.message);
                })
                .finally(function () {
                  // Apply the changes to update the view
                  $scope.$apply();
                });
            } catch (err) {
              ErrorNotificationService.showDefaultErrorDialog(err.message);
            }
          };

          const fetchCaptionStatus = async (cuids) => {
            const maxRetries = 5;
            const retryDelay = 5000;
            let retries = 0;
            while (retries < maxRetries) {
              try {
                const resdata = await getCaptionStatuses(cuids);
                const captionStatusesByCuids =
                  resdata.data.captionStatusesByCuids;
                const invalidCuids = resdata.data.invalidCuids;

                if (invalidCuids.length) {
                  ToastNotificationService.error({
                    title: 'Error',
                    type: ToastType.ERROR,
                    message:
                      'The requested video is not available to turn on captions ',
                    options: { ...defaultToastOptions, closeButton: false },
                  });
                  $scope.model.isProcessingCaption = false;
                  return 'failed';
                }

                const captionStatus = Object.values(captionStatusesByCuids)[0];

                if (captionStatus === CAPTIONS_STATUS_VALUE.IN_PROGRESS) {
                  retries++;
                  await new Promise((resolve) =>
                    setTimeout(resolve, retryDelay),
                  );
                } else if (captionStatus === 'Failed') {
                  $scope.$applyAsync(() => {
                    $scope.model.isProcessingCaption = false;
                  });
                  ToastNotificationService.close();
                  ToastNotificationService.error({
                    title: 'Error',
                    type: ToastType.ERROR,
                    message: `We weren't able to turn on captions. Please try again.`,
                    options: { ...defaultToastOptions, closeButton: false },
                  });
                  return 'failed';
                } else if (captionStatus === CAPTIONS_STATUS_VALUE.SUCCEEDED) {
                  $scope.$applyAsync(() => {
                    $scope.model.isProcessingCaption = false;
                  });
                  ToastNotificationService.close();
                  getMediaItemMetadata(launchOptions.getUniversalItemId()).then(
                    (itemMetadata) => {
                      $ngRedux.dispatch(
                        SelectedItemActions.receiveItemMetadata(itemMetadata),
                      );
                      $scope.model.isVideoCaptioned =
                        itemMetadata.isVideoCaptioned;
                      $scope.model.captionsLink = $sce.trustAsResourceUrl(
                        itemMetadata?.video?.captionsLink,
                      );
                    },
                  );
                  ToastNotificationService.success({
                    title: 'Success',
                    type: ToastType.SUCCESS,
                    message: 'Captions turned on successfully.',
                    options: { ...defaultToastOptions, closeButton: false },
                  });
                  return 'success';
                } else {
                  retries++;
                  await new Promise((resolve) =>
                    setTimeout(resolve, retryDelay),
                  );
                }
              } catch (error) {
                retries++;
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
              }
            }

            ToastNotificationService.error({
              title: 'Error',
              type: ToastType.ERROR,
              message: `We weren't able to turn on captions. Please try again.`,
              options: { ...defaultToastOptions, closeButton: false },
            });
            $scope.$applyAsync(() => {
              $scope.model.isProcessingCaption = false;
            });
            return 'unknown';
          };

          $scope.model.clickCaptionButton = function () {
            $scope.$applyAsync(() => {
              $scope.model.isProcessingCaption = true;
            });

            const cuids = launchOptions.getUniversalItemId().split(' ');
            const isVideoCaptioned = !$scope.model.isVideoCaptioned;
            const captionValue = isVideoCaptioned ? 'on' : 'off';
            let options;
            options = defaultToastOptions;
            processCaptioningOnSelectedItems(isVideoCaptioned, cuids)
              .then((res) => {
                if (isVideoCaptioned) {
                  ToastNotificationService.success({
                    title: 'In Progress',
                    type: ToastType.INFO,
                    message: `Turning ${captionValue} captions...`,
                    options: _.extend({}, options, {
                      closeButton: false,
                    }),
                  });
                  fetchCaptionStatus(cuids);
                } else {
                  $scope.$applyAsync(() => {
                    $scope.model.isProcessingCaption = false;
                  });

                  $scope.model.isVideoCaptioned = false;
                  ToastNotificationService.success({
                    title: 'Success',
                    type: ToastType.SUCCESS,
                    message: 'Captions turned off successfully.',
                    options: _.extend({}, options, {
                      closeButton: false,
                    }),
                  });
                }
              })
              .catch((error) => {
                ErrorNotificationService.showDefaultErrorDialog(error);
              });
          };

          $scope.model.shouldShowShowroomPreview = function () {
            const currentWorkflow =
              UniversalImageActionsService.getCurrentWorkflow();
            const tributaryV2Workflows = [
              UniversalImageActionsView.TributaryCustomizeProducts.name,
              UniversalImageActionsView.TributaryCustomizeMedia.name,
              UniversalImageActionsView.TributaryAddOns.name,
              UniversalImageActionsView.TributaryCustomizeLinks.name,
            ];
            const onTributaryPanel =
              currentWorkflow &&
              _.contains(
                tributaryV2Workflows,
                currentWorkflow.getCurrentState(),
              );
            return (
              $scope.model.isShowroomSelfServeV2Enabled && onTributaryPanel
            );
          };

          $scope.model.stopAndPreventDefault = function ($event) {
            $event.preventDefault();
            $event.stopPropagation();
          };

          $scope.model.recordDownloadEvent = function () {
            TrackingService.recordEvent(
              TRACKING_CATEGORY,
              TrackingService.ACTION_DOWNLOAD,
            );
            return true;
          };

          $scope.model.hoverClose = function (hovering) {
            $scope.model.mouseOverCloseButton = hovering;
          };

          $scope.model.downloadTriggered = function () {
            UniversalImageActionsService.trackMediaEvent(
              MediaTrackingEventName.ItemDownloaded,
            );

            $scope.$applyAsync(() => {
              $scope.model.isProcessingDownload = true;
            });

            return angular.noop;
          };

          $scope.model.downloadStarted = function () {
            $scope.$applyAsync(() => {
              $scope.model.isProcessingDownload = false;
            });
            return angular.noop;
          };
          $scope.model.getDistributeButtonClass = function () {
            let panelsAvailable = 0;

            if ($scope.model.isSchedulerEnabled) {
              panelsAvailable++; // Schedule Now
            }

            if ($scope.model.clientHasLibraryAccess) {
              panelsAvailable++; // Add to Library
            }

            if ($scope.model.isOnePageSelfServeEnabled) {
              panelsAvailable++; // ONE Page
            }

            if ($scope.model.fanreels && $scope.model.fanreels.length > 0) {
              panelsAvailable++; // Approve for Fanreel
            }

            if ($scope.model.isRevealEnabled) {
              panelsAvailable++; // Reveal
            }

            if ($scope.model.isTributaryEnabled) {
              panelsAvailable++; // Tributary
            }

            switch (panelsAvailable) {
              case 1:
                return 'one';
              case 2:
                return 'two';
              case 3:
                return 'three';
              case 4:
                return 'four';
              case 5:
                return 'five';
              case 6:
                return 'six';
              default:
                return '';
            }
          };

          $rootScope.$on('$locationChangeStart', ($event, newUrl, oldUrl) => {
            // If the "openItem" parameter is being removed from the URL, we should close.
            // (We are either navigating away, or the user hit the back button)
            if (
              oldUrl.indexOf('openItem') >= 0 &&
              newUrl.indexOf('openItem') === -1 &&
              isUiaPermalinksEnabled
            ) {
              $scope.model.close();
            }
          });

          $scope.model.uiaLoadableComplete = function () {
            $window.addEventListener('resize', calculateTotalMediaSize);
            calculateTotalMediaSize();
          };

          $scope.model.launchToView = function (view, workflow) {
            switch (view) {
              case UniversalImageActionsView.ApproveForFanreel.name:
              case UniversalImageActionsView.ApproveForGalleries.name:
              case UniversalImageActionsView.ScheduleNow.name:
                $scope.model.activateWorkflow(view);
                break;
              case UniversalImageActionsView.Home.name:
              case UniversalImageActionsView.Labels.name:
              case UniversalImageActionsView.Notes.name:
              case UniversalImageActionsView.Productize.name:
              case UniversalImageActionsView.Reveal.name:
              case UniversalImageActionsView.OnePage.name:
              case UniversalImageActionsView.RightsRequest.name:
              case UniversalImageActionsView.Details.name:
              case UniversalImageActionsView.TributaryCustomizeProducts.name:
              case UniversalImageActionsView.TributaryCustomizeMedia.name:
              case UniversalImageActionsView.TributaryCustomizeLinks.name:
                if (workflow) {
                  workflow =
                    UniversalImageActionsWorkflowService.workflowForName(
                      workflow,
                    );
                  UniversalImageActionsService.setCurrentWorkflow(workflow);
                  goToWorkflowStep(view);
                } else {
                  $state.go(view);
                }
                break;
              default:
                break;
            }
          };

          $scope.model.launchOnePageWorkflow = function () {
            if ($scope.model.productsCount > 0) {
              $scope.model.launchToView(
                UniversalImageActionsView.OnePage.name,
                UniversalImageActionWorkflows.CreateOnePage,
              );
            } else {
              $scope.model.launchToView(
                UniversalImageActionsView.Productize.name,
                UniversalImageActionWorkflows.CreateOnePage,
              );
            }
          };

          $scope.model.launchShowroomWorkflow = function () {
            if ($scope.model.isShowroomSelfServeV2Enabled) {
              const launchToAppropriateShowroomPanel = () => {
                if (
                  $scope.model.showroomHasBeenUpdated &&
                  $scope.model.productsCount > 0
                ) {
                  $scope.model.launchToView(
                    UniversalImageActionsView.TributaryCustomizeLinks.name,
                    UniversalImageActionWorkflows.CreateAdaptiveLink,
                  );
                } else if ($scope.model.productsCount > 0) {
                  $scope.model.launchToView(
                    UniversalImageActionsView.TributaryCustomizeProducts.name,
                    UniversalImageActionWorkflows.CreateAdaptiveLink,
                  );
                } else {
                  $scope.model.launchToView(
                    UniversalImageActionsView.Productize.name,
                    UniversalImageActionWorkflows.CreateAdaptiveLink,
                  );
                }
              };

              if (angular.isDefined($scope.model.showroomHasBeenUpdated)) {
                launchToAppropriateShowroomPanel();
              } else {
                $scope.model.loading = true;
                showroomPromise
                  .then(() => {
                    launchToAppropriateShowroomPanel();
                  })
                  .finally(() => {
                    $scope.model.loading = false;
                  });
              }
            } else {
              $scope.model.launchToView(
                UniversalImageActionsView.Productize.name,
                UniversalImageActionWorkflows.CreateAdaptiveLink,
              );
            }
          };

          $scope.model.setDisplayImage = function (url) {
            return $q((resolve) => {
              $scope.model.imageToLoad = new Image(); // put this on the scope to mock it during tests

              $scope.model.imageToLoad.onload = () => {
                desiredHeight = $scope.model.imageToLoad.height;
                desiredWidth = $scope.model.imageToLoad.width;
                desiredAspectRatio = desiredWidth / desiredHeight;

                $scope.model.displayImageUrl = url;

                calculateTotalMediaSize();

                resolve();
              };

              $scope.model.imageToLoad.src = url;
            });
          };

          //
          // Initialization
          //

          $scope.model.launchToView(launchView, launchWorkflow);

          $ngRedux.dispatch(setLaunchOptions(launchOptions));

          LabelsService.labels.subscribe((newLabels) => {
            $scope.$applyAsync(() => {
              $scope.model.labelCount = newLabels.length;
              $scope.model.labels = newLabels;
            });
          });

          let iptV2Subscription = null;
          let iptV2ErrorSubscription = null;
          const cleanupIptV2 = () => {
            $scope.$applyAsync(() => {
              $scope.model.isIptV2Loading = false;
            });
            if (
              iptV2Subscription &&
              typeof iptV2Subscription.unsubscribe === 'function'
            ) {
              iptV2Subscription.unsubscribe();
              iptV2Subscription = null;
            }
            if (
              iptV2ErrorSubscription &&
              typeof iptV2ErrorSubscription.unsubscribe === 'function'
            ) {
              iptV2ErrorSubscription.unsubscribe();
              iptV2ErrorSubscription = null;
            }
          };

          $scope.$on('$destroy', cleanupIptV2);

          if (isIntelligentProductTaggingEnabled) {
            const itemId = launchOptions.getUniversalItemId();
            const relevancyObservable =
              AssistedProductTaggingService.getProductRelevancyDataSubject(
                itemId,
              );
            const relevancyErrorObservable =
              AssistedProductTaggingService.getProductRelevancyErrorSubject(
                itemId,
              );

            if (
              relevancyObservable &&
              typeof relevancyObservable.subscribe === 'function'
            ) {
              $scope.$applyAsync(() => {
                $scope.model.isIptV2Loading =
                  isIntelligentProductTaggingV2Enabled;
              });

              iptV2Subscription = relevancyObservable
                .take(1)
                .subscribe(cleanupIptV2);

              if (
                relevancyErrorObservable &&
                typeof relevancyErrorObservable.subscribe === 'function'
              ) {
                iptV2ErrorSubscription = relevancyErrorObservable
                  .take(1)
                  .subscribe(() => {
                    cleanupIptV2();
                    ToastNotificationService.error({
                      title: 'Error',
                      type: ToastType.ERROR,
                      message:
                        "We couldn't load product suggestions. Please try again.",
                      options: { ...defaultToastOptions, closeButton: false },
                    });
                  });
              }
            }
          }

          if ($scope.model.isShowroomSelfServeV2Enabled) {
            const itemId = launchOptions.getUniversalItemId();
            const showroomContext =
              launchOptions.getShowroomLaunchContext() || {};
            const implType = showroomContext.implementationType;
            const sourceId = showroomContext.sourceId;

            showroomPromise = ShowroomApiService.doesTributaryImplExistAsync(
              itemId,
              implType,
              sourceId,
            ).then((result) => {
              $scope.model.showroomExists = result.implExists;
              $scope.model.showroomHasBeenUpdated = result.hasBeenUpdated;
              $scope.model.showroomHasProducts = result.showroomHasProducts;
              // Since we may skip the productize panel, set the number of tagged products beforehand
              UniversalImageActionsService.setNumTaggedProducts(
                result.numProducts,
              );
            });
          }

          const itemIds = [launchOptions.getUniversalItemId()];
          ContentLibraryApiService.getContentItemsAsync({ itemIds }).then(
            (response) => {
              const item = response.contentItems[0];
              if (!item) {
                return;
              }

              $scope.model.isGif = ImageMetadataService.isGif(item);
              $scope.model.isMuted =
                item.metadata.videoMuteStatus == VIDEO_MUTESTATUS
                  ? true
                  : false;
            },
          );

          const getMetadata = getMediaItemMetadata(
            launchOptions.getUniversalItemId(),
          );

          getMetadata.then((itemMetadata) => {
            let video;
            $ngRedux.dispatch(
              SelectedItemActions.receiveItemMetadata(itemMetadata),
            );
            $scope.model.isVideoCaptioned = itemMetadata.isVideoCaptioned;
            const muteStatus =
              itemMetadata.videoSource?.indexOf(VIDEO_MUTESTATUS) > 0
                ? true
                : false;
            // call initialize after the metadata is retrieved and cached
            LabelsService.initialize(launchOptions.getUniversalItemId());

            $scope.model.canonicalImageUrl = itemMetadata.imageUrl;
            $scope.model.displayImageUrl =
              itemMetadata.displayImageUrl || itemMetadata.imageUrl;
            $scope.model.productsCount = itemMetadata.productTags.length;
            $scope.model.itemMetadata = itemMetadata;
            $scope.model.originalUrl = itemMetadata.originalUrl;
            $scope.model.notesCount = itemMetadata.notesCount;

            ProductTaggingService.setProducts(
              itemMetadata.productTags,
              launchOptions.getUniversalItemId(),
            );

            // Get campaign ID by parsing the current URL
            let campaignId = null;
            const currentUrl = $location.absUrl();

            // Match patterns like /campaigns/sampling/campaign/12345/
            const campaignMatch = currentUrl.match(
              /\/campaigns\/sampling\/campaign\/(\d+)/,
            );

            const { isBvCampaignTagged } = itemMetadata;

            if (campaignMatch && campaignMatch[1]) {
              campaignId = campaignMatch[1];
            }

            // Only check campaign perspective if item is BV campaign tagged and we have a campaign ID
            if (campaignId && isBvCampaignTagged) {
              CampaignPerspectiveService.isBrandPerspectiveView(campaignId)
                .then((isBrandPerspective) => {
                  $scope.$applyAsync(() => {
                    $scope.model.isBrandPerspectiveView = isBrandPerspective;
                  });
                })
                .catch((error) => {
                  $scope.model.isBrandPerspectiveView = false;
                });
            } else {
              // Not BV campaign tagged or not in a campaign context
              $scope.model.isBrandPerspectiveView = false;
            }

            if (itemMetadata && itemMetadata.videoSource) {
              $scope.model.videoSource = $sce.trustAsResourceUrl(
                itemMetadata.videoSource,
              );
              $scope.model.captionsLink = $sce.trustAsResourceUrl(
                itemMetadata?.video?.captionsLink,
              );

              const downloadUrlPromise = isDefined(itemMetadata.videoServiceId)
                ? UniversalImageActionsApiService.getVideoDownloadUrl(
                    itemMetadata.videoServiceId,
                    muteStatus,
                  )
                : Promise.resolve(itemMetadata.videoSource);

              downloadUrlPromise.then((downloadUrl) => {
                setDownloadDetails(
                  downloadUrl,
                  UniversalImageActionsApiService.getVideoDownloadFilename(
                    itemMetadata.socialDetails.networkUsername,
                  ),
                );
              });

              video = angular.element('#uia-video').get(0);
              video.addEventListener('loadedmetadata', () => {
                videoWidth = video.videoWidth;
                videoHeight = video.videoHeight;
                videoAspectRatio = videoWidth / videoHeight;
                $scope.model.videoDuration = video.duration
                  ? moment()
                      .startOf('day')
                      .seconds(video.duration)
                      .format('HH:mm:ss')
                  : undefined;

                calculateTotalMediaSize();

                $scope.$apply(() => {
                  $scope.model.isVideoReady = true;
                  if (launchOptions.autoPlayVideo) {
                    video.play();
                  }
                });
              });
              video.addEventListener('error', () => {
                ErrorNotificationService.showDefaultErrorDialog(
                  'Source video could not be found.',
                );
              });
            } else if (itemMetadata && !itemMetadata.videoSource) {
              setDownloadDetails(
                itemMetadata.imageUrl,
                UniversalImageActionsApiService.getImageDownloadFilename(
                  itemMetadata.socialDetails.networkUsername,
                ),
              );
            } else {
              $scope.$apply(() => {
                // Since it could not be determined if this is a video or image, hide the download icon by not setting a downloadUrl
                $scope.model.hasFetchedDownloadUrl = true;
              });
            }

            if (launchReel) {
              UniversalImageActionsService.setSelectedReel(launchReel);
            }

            if (itemMetadata.socialNetwork === 'TikTok') {
              $scope.model.isRevealEnabled = false;
              $scope.model.isSchedulerEnabled = false;
              UniversalImageActionsApiService.getTikTokItem(
                itemMetadata.rootCuid,
              )
                .then(async (item) => {
                  // TODO: PD-249571 - Replace this logic derived media type from network item when added

                  // Currently TikTok embeds do not support photo carousels
                  // If the item is coming from a connected TikTok account, we can check the duration
                  // to determine if it is a video or not. If the item is coming from a TikTok Creator
                  // Marketplace user (TTCM), the TikTok API does not provide durationSeconds. In this case
                  // we can check the oembed endpoint to determine if the item is a video or not.
                  // we will fallback to the cover image if the item is not a video
                  let isUseableEmbed =
                    item.networkItem.networkData?.durationSeconds > 0;

                  if (!isUseableEmbed) {
                    try {
                      await fetch(
                        `https://tiktok.com/oembed?url=${item.networkItem.networkUrl}`,
                        { mode: 'no-cors' },
                      );
                      isUseableEmbed = true;
                    } catch (error) {
                      isUseableEmbed = false;
                    }
                  }
                  const embedInfo = item
                    ? {
                        durationSeconds:
                          item.networkItem.networkData?.durationSeconds,
                        embedUrl: isUseableEmbed
                          ? `https://www.tiktok.com/player/v1/${item.networkItem.id}?music_info=1&description=1&autoplay=1&loop=1`
                          : item.networkItem.networkData?.embedUrl,
                        title: formatTikTokEmbedTitle(item),
                      }
                    : undefined;
                  if (isUseableEmbed) {
                    $scope.$applyAsync(() => {
                      $scope.model.loading = false;
                      $scope.model.embed = {
                        link: $sce.trustAsResourceUrl(embedInfo.embedUrl),
                        aspectRatio: TIKTOK_ASPECT_RATIO,
                        title: embedInfo.title,
                      };
                    });
                  } else {
                    $scope.model.embed = undefined;
                    $scope.model
                      .setDisplayImage(itemMetadata.displayImageUrl)
                      .then(() => {
                        $scope.$applyAsync(() => {
                          $scope.model.loading = false;
                        });
                      });
                  }
                })
                .catch((err) => {
                  console.error('Unable to fetch tiktok embed', err);
                  $scope.model.embed = undefined;
                  $scope.model
                    .setDisplayImage(itemMetadata.displayImageUrl)
                    .then(() => {
                      $scope.$applyAsync(() => {
                        $scope.model.loading = false;
                      });
                    });
                });
            } else if (itemMetadata.socialNetwork === 'YouTube') {
              $scope.model.isRevealEnabled = false;
              $scope.model.isSchedulerEnabled = false;
              UniversalImageActionsApiService.getYouTubeItem(
                itemMetadata.rootCuid,
              )
                .then(async (item) => {
                  // TODO: PD-249571 - Replace this logic derived media type from network item when added
                  const embedInfo = item
                    ? {
                        embedUrl: YouTubeWebLinks.getEmbedLink(
                          item.networkItem.id,
                        ),
                        title: formatYouTubeEmbedTitle(item),
                      }
                    : undefined;
                  $scope.$applyAsync(() => {
                    $scope.model.loading = false;
                    $scope.model.embed = {
                      link: $sce.trustAsResourceUrl(embedInfo.embedUrl),
                      aspectRatio: YOUTUBE_ASPECT_RATIO,
                      title: embedInfo.title,
                    };
                  });
                })
                .catch((err) => {
                  console.error('Unable to fetch youtube embed', err);
                  $scope.model.embed = undefined;
                  $scope.model
                    .setDisplayImage(itemMetadata.displayImageUrl)
                    .then(() => {
                      $scope.$applyAsync(() => {
                        $scope.model.loading = false;
                      });
                    });
                });
            } else {
              $scope.model
                .setDisplayImage(itemMetadata.displayImageUrl)
                .then(() => {
                  $scope.$applyAsync(() => {
                    $scope.model.loading = false;
                  });
                });
            }
            if (itemMetadata) {
              const existingRightsPermissionsSource =
                itemMetadata.rightsPermissions &&
                itemMetadata.rightsPermissions.creationSource;

              const preapprovedSource = [
                'brandowned',
                'RelationshipClientToClient',
                'RelationshipInfluencerToClient',
                'SkippedRightsRequest-BVImport',
                'SkippedRightsRequest',
              ];

              // Skip URL check if image doesn't require a rights message to be sent
              // e.g. An Influencer creation source doesn't require a message to be sent
              // as they already have auto-rights configured, so it is skipped
              if (
                !preapprovedSource.includes(existingRightsPermissionsSource)
              ) {
                checkRightsMessageForTermsAndConditionsUrl();
              }
            }
          });

          $scope.model.outstandingAsyncAction = 'getReels';
          UniversalImageActionsService.getCachedReelsAsync()
            .then((reels) => {
              $scope.model.fanreels = reels;
              updateDistributionOptions();
            })
            .finally(() => {
              $scope.model.outstandingAsyncAction = null;
            });

          // Distribution Options
          $scope.model.isSocialMediaOptionEnabled =
            $scope.model.isSchedulerEnabled &&
            $scope.model.areSocialDashboardsEnabled;

          // Initialize as false because these values will be set once
          // fanreels have been loaded
          $scope.model.isApproveForFanreelOptionEnabled = false;
          $scope.model.isApproveForGalleriesOptionEnabled = false;

          $scope.model.isApproveForSyndicationOptionEnabled =
            $scope.model.isBazaarvoiceSyndicationOnlyClient ||
            $scope.model.isBazaarvoiceNetworkSyndicationOnlyFullClient;

          $scope.model.isOnePageOptionEnabled =
            $scope.model.isOnePageSelfServeEnabled;

          $scope.model.isRevealOptionEnabled = $scope.model.isRevealEnabled;

          $scope.model.isShowroomOptionEnabled =
            $scope.model.isTributaryEnabled;

          $scope.model.isMediaLibraryOptionEnabled =
            $scope.model.clientHasLibraryAccess &&
            !$scope.model.isBazaarvoiceSyndicationOnlyClient &&
            !$scope.model.isBazaarvoiceNetworkSyndicationOnlyFullClient;

          $scope.model.hasDistributionOptions =
            $scope.model.isSocialMediaOptionEnabled ||
            $scope.model.isApproveForFanreelOptionEnabled ||
            $scope.model.isApproveForGalleriesOptionEnabled ||
            $scope.model.isApproveForSyndicationOptionEnabled ||
            $scope.model.isOnePageOptionEnabled ||
            $scope.model.isRevealOptionEnabled ||
            $scope.model.isShowroomOptionEnabled ||
            $scope.model.isMediaLibraryOptionEnabled;

          function updateDistributionOptions() {
            $scope.model.isApproveForFanreelOptionEnabled =
              !$scope.model.isGalleriesEnabled &&
              !$scope.model.isInstaShopEnabled &&
              $scope.model.fanreels &&
              $scope.model.fanreels.length > 0 &&
              !$scope.model.isBazaarvoiceSyndicationOnlyClient;

            $scope.model.isApproveForGalleriesOptionEnabled =
              ($scope.model.isGalleriesEnabled ||
                $scope.model.isInstaShopEnabled) &&
              $scope.model.fanreels &&
              ($scope.model.isBazaarvoiceSyndicationEnabled ||
                $scope.model.fanreels.length > 0);
          }

          this.resetAnimations();

          this.currentlyInWorkflow = function () {
            return $scope.model.currentlyInWorkflow;
          };

          // Event Handlers

          $scope.$on('uia:modal:image-url-changed', ($event, newImage) => {
            $scope.model.setDisplayImage(
              newImage.displayImageUrl || newImage.canonicalImageUrl,
            );
            $scope.model.canonicalImageUrl = newImage.canonicalImageUrl;
          });

          $scope.$on('uia:modal:close', ($event, data) => {
            const result = data ? data.result : undefined;
            $scope.model.close(result);
          });

          // Update the rights status icon after requesting rights or cancelling queued rights request
          $scope.$on(
            'uia:rights-request-status-changed',
            ($event, newStatus) => {
              const currentStatus =
                $scope.model.itemMetadata.rightsRequestStatus.status;
              if (
                (newStatus === RightsStatus.Pending ||
                  newStatus === RightsStatus.Initiated) &&
                (currentStatus === RightsStatus.Queued ||
                  currentStatus === RightsStatus.Unsent ||
                  currentStatus === RightsStatus.FailedToSend ||
                  currentStatus === RightsStatus.Rejected)
              ) {
                $scope.model.itemMetadata.rightsRequestStatus = {
                  status: RightsStatus.Pending,
                };
              } else if (newStatus === RightsStatus.Queued) {
                $scope.model.itemMetadata.rightsRequestStatus = {
                  status: RightsStatus.Queued,
                };
              } else if (
                newStatus === RightsStatus.None &&
                currentStatus === RightsStatus.Queued
              ) {
                $scope.model.itemMetadata.rightsRequestStatus = {
                  status: RightsStatus.None,
                };
              }
            },
          );
          $scope.$on('uia:rights-permission-changed', () => {
            const partialCuid = launchOptions.getUniversalItemId();
            getMediaItemMetadata(partialCuid).then((metadata) => {
              $scope.$evalAsync(function () {
                $scope.model.itemMetadata = {
                  ...metadata,
                };
              });
            });
          });
          $scope.$on('uia:products-changed', ($event, products) => {
            $scope.model.productsCount = products.length;
          });

          $scope.$on('uia:notes-changed', ($event, newNotes) => {
            $scope.model.notesCount = newNotes.length;
          });

          $scope.$on('beautify-button:Closed', () => {
            $scope.model.beautifyOpen = false;
          });

          $scope.$on(
            '$stateChangeSuccess',
            (event, toState, toParams, fromState) => {
              const conditionallyPrefixName = (name) => {
                const workflow =
                  UniversalImageActionsService.getCurrentWorkflow();

                // If we are within a workflow, prefix that onto the state
                if (workflow && name.startsWith('distribute')) {
                  return `${workflow.name}.${name.replace(
                    /distribute\./g,
                    '',
                  )}`;
                } else {
                  return name;
                }
              };
              const fromStateName = conditionallyPrefixName(fromState.name);
              const toStateName = conditionallyPrefixName(toState.name);

              TrackingService.recordEvent(
                TRACKING_CATEGORY,
                TrackingService.ACTION_VIEW_PANEL,
                fromStateName
                  ? `${fromStateName}=>${toStateName}`
                  : toStateName,
              );
            },
          );
        },
      ],
      link($scope, element) {
        $scope.model.playVideo = function () {
          const video = element[0].querySelector('#uia-video');

          if (video && video.readyState) {
            video.play();
          }
        };
      },
    };
  });
})();
